/**
 * RammyPingToast.jsx — the "living-form" toast used whenever Rammy proactively
 * reaches out to the user (morning brief, an emotional check-in seed, a
 * reminder that just came due, a new memory extracted, etc).
 *
 * Design intent (per user):
 *   • Slides in from the bottom-right corner.
 *   • Uses the same .rammy-orb signature form — the orb breathes warmly.
 *   • Holds for ~4 s, then fades out. Hovering pauses the timer.
 *   • Optional primary action ("Open", "Snooze", "Later"…) — invoking it
 *     dismisses the toast. Clicking the surface anywhere also dismisses.
 *   • Never intrusive: only ONE ping is visible at a time; a small counter
 *     shows how many more are queued behind it.
 *
 * External contract:
 *   <RammyPingHost/>                — mount once in AppShell.
 *   pushRammyPing({title, body, mood, action})
 *                                  — imperative fire from anywhere.
 *
 *   • `mood` : "idle" | "thinking" | "speaking" | "sad"  (drives orb tint)
 *   • `action` : { label, onClick } — optional CTA rendered inside the toast.
 *
 * Kept dependency-free (no framer-motion) so it slots into the existing
 * stack cleanly.
 */
import { useEffect, useRef, useState, useCallback } from "react";

// ─── Module-level pub/sub ─────────────────────────────────────────────────
// We use a tiny in-module event bus so any component in the tree can push
// a ping without needing React context. The host subscribes once at mount.
const _listeners = new Set();
export function pushRammyPing(msg) {
  const id = msg.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const payload = { id, ...msg };
  _listeners.forEach((fn) => { try { fn(payload); } catch (_) { /* noop */ } });
  return id;
}

// ─── The host ─────────────────────────────────────────────────────────────
export function RammyPingHost({ position = "bottom-right", holdMs = 4000 }) {
  const [current, setCurrent] = useState(null);
  const [queue, setQueue] = useState([]);
  const [entering, setEntering] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  const seenIdsRef = useRef(new Set());
  const timerRef = useRef(null);

  // Subscribe on mount.
  useEffect(() => {
    const handler = (msg) => {
      if (seenIdsRef.current.has(msg.id)) return; // duplicate — ignore
      seenIdsRef.current.add(msg.id);
      setQueue((q) => [...q, msg]);
    };
    _listeners.add(handler);
    return () => { _listeners.delete(handler); };
  }, []);

  // Promote next from queue when we're idle.
  useEffect(() => {
    if (!current && queue.length) {
      const [next, ...rest] = queue;
      setQueue(rest);
      setCurrent(next);
      setEntering(true);
      // let the enter transition run for one frame before releasing.
      requestAnimationFrame(() => setEntering(false));
    }
  }, [current, queue]);

  // Auto-dismiss timer.
  useEffect(() => {
    if (!current || paused) { clearTimeout(timerRef.current); return; }
    timerRef.current = setTimeout(() => setLeaving(true), holdMs);
    return () => clearTimeout(timerRef.current);
  }, [current, paused, holdMs]);

  // When leave animation completes, unmount + let queue promote the next.
  const onLeaveEnd = useCallback(() => {
    if (!leaving) return;
    setLeaving(false);
    setCurrent(null);
  }, [leaving]);

  const dismiss = () => setLeaving(true);
  const invokeAction = () => {
    try { current?.action?.onClick?.(current); } catch (_) { /* noop */ }
    dismiss();
  };

  if (!current) return null;

  const mood = current.mood || "speaking";
  const posClass = position === "bottom-right"
    ? "right-5 bottom-5"
    : position === "top-right"
      ? "right-5 top-5"
      : "left-5 bottom-5";

  return (
    <div
      className={`fixed z-[60] ${posClass}`}
      style={{
        opacity: entering ? 0 : (leaving ? 0 : 1),
        transform: `translateY(${entering || leaving ? "14px" : "0"}) scale(${leaving ? 0.97 : 1})`,
        transition: "opacity 320ms ease, transform 380ms cubic-bezier(0.22,0.61,0.36,1)",
        pointerEvents: "auto",
      }}
      onTransitionEnd={onLeaveEnd}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="rammy-ping-toast"
    >
      <div
        className="relative flex items-center gap-3 pl-3 pr-4 py-3 rounded-2xl bg-[var(--rm-surface)]/95 backdrop-blur-md shadow-[0_18px_40px_-14px_rgba(26,83,92,0.35)] ring-1 ring-black/[0.04] max-w-[360px]"
        role="status"
        aria-live="polite"
      >
        {/* Ambient turquoise glow behind the orb — extra "presence" */}
        <div
          aria-hidden
          className="absolute -left-2 -top-2 w-16 h-16 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(98,203,196,0.35) 0%, rgba(98,203,196,0) 65%)",
            filter: "blur(8px)",
          }}
        />
        <div className="rammy-orb rammy-orb--sm shrink-0 relative" data-mood={mood} aria-hidden />
        <div className="flex-1 min-w-0">
          {current.title && (
            <div className="text-[12px] font-semibold text-[var(--rammy-cyan-dark)] leading-tight truncate">
              {current.title}
            </div>
          )}
          {current.body && (
            <div className="text-[13.5px] text-[var(--rammy-text)] leading-snug mt-0.5 line-clamp-2">
              {current.body}
            </div>
          )}
          {current.action?.label && (
            <button
              onClick={invokeAction}
              className="mt-1.5 text-[12px] font-medium text-[var(--rammy-cyan-dark)] hover:underline underline-offset-2"
              data-testid="rammy-ping-action"
            >
              {current.action.label} →
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-[11px] text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] shrink-0 px-1"
          aria-label="Dismiss"
          data-testid="rammy-ping-dismiss"
        >
          ×
        </button>
        {/* Progress hairline showing the auto-dismiss timer. Pauses when hovered. */}
        <div className="absolute bottom-0 left-3 right-3 h-[1.5px] rounded-full bg-[var(--rammy-cyan)]/15 overflow-hidden">
          <div
            key={current.id + (paused ? "-pause" : "-run")}
            className="h-full bg-[var(--rammy-cyan)]"
            style={{
              width: "100%",
              transform: leaving ? "scaleX(0)" : (paused ? "scaleX(1)" : "scaleX(0)"),
              transformOrigin: "left",
              transition: paused ? "none" : `transform ${holdMs}ms linear`,
            }}
          />
        </div>
      </div>
      {queue.length > 0 && (
        <div className="text-[10px] text-[var(--rammy-text-muted)] mt-1 pl-3">
          +{queue.length} more
        </div>
      )}
    </div>
  );
}

export default RammyPingHost;
